import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    DynamicFormMobileFilterComponent,
    EmptyState,
    ListFilterMultipleSelect,
    ListFilterMultipleSelectField,
    ListItemElement,
    Monogram,
    RowAnchorModel,
    SelectedFilter,
    SimpleFilterModel,
    SkeletonLoaderModel,
    StandardListPageModel
} from '@symplicity/syng';
import { BehaviorSubject, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import * as _ from 'lodash';
import {
    DEFAULT_PAGE,
    DEFAULT_PER_PAGE,
    EventRegistration,
    EventRegistrationsResponse,
    EventsRegistrationParams,
    EventsService
} from '../../services';
import { EventAttendingEmployersData } from '../event-view/event-view.resolvers';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BsDropdownDirective } from 'ngx-bootstrap/dropdown';
import { TranslateService } from '@ngx-translate/core';

interface EmployersItems extends EventRegistrationsResponse {
    items: StandardListPageModel[];
}

@Component({
    selector: 'cf-event-attending-employers',
    templateUrl: './event-attending-employers.component.html'
})
export class EventAttendingEmployersComponent {
    @ViewChild(BsDropdownDirective, {static:false})
    public dropdown: BsDropdownDirective;

    public readonly resolvedData = this.route.snapshot.data.resolvedData as EventAttendingEmployersData;
    public readonly eventId = this.resolvedData.eventId;
    public readonly eventStatus = this.resolvedData.eventStatus;
    public readonly isVirtualFair = this.resolvedData.isVirtualFair;
    public readonly isLawCsm = this.resolvedData.isLawCsm;
    public readonly filtersFormConfig = this.resolvedData.filtersFormConfig;
    public readonly rowAnchor = new RowAnchorModel({
        onClick: (event) => this.onRowAnchorClick(event.item.id),
        enable: false
    });
    public readonly skeleton = new SkeletonLoaderModel({
        thumbnails: true,
        thumbnailsStyle: '',
        texts: 3
    });
    public readonly emptyState = new EmptyState({});
    public readonly filters = new SimpleFilterModel({
        filters: [],
        submitFn: this.searchByKeywords.bind(this)
    });
    public readonly showHeader$ = new BehaviorSubject<boolean>(true);
    public readonly items$ = new Subject<StandardListPageModel[]>();
    public readonly page$ = new Subject<number>();
    public readonly perPage$ = new Subject<number>();
    public readonly totalCount$ = new Subject<number>();
    public readonly searchText$ = new BehaviorSubject<string>('');
    public selectedFilters: SelectedFilter[] = [];
    public displayMoreFilter = true;
    public params: EventsRegistrationParams = {
        page: DEFAULT_PAGE,
        perPage: DEFAULT_PER_PAGE
    };

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private eventsService: EventsService,
        private window: Window,
        private modalService: NgbModal,
        private translationsService: TranslateService
    ) {
        this.filters.filters = this.filters.filters.concat(this.getSimpleFilters());
        this.displayMoreFilter = !!this.filtersFormConfig.controls.find(control => !control.isSimpleFilter);

        this.getEmployers();
    }

    public onReload(params: any): void {
        const { page, perPage } = params;

        this.params.page = page;
        this.params.perPage = perPage;

        this.getEmployers();
    }

    private getEmployers(): void {
        const params = this.getApiParams();

        this.rowAnchor.enable = false;

        this.eventsService.getEventRegistrations(this.eventId, params).pipe(
            map(response => this.getItems(response))
        ).subscribe(
            response => {
                const isFromSearch = !!this.selectedFilters.length;

                this.showHeader$.next(!!response.items.length || !response.items.length && isFromSearch);
                this.items$.next(response.items);
                this.page$.next(response.page);
                this.perPage$.next(response.perPage);
                this.totalCount$.next(response.total);

                this.rowAnchor.enable = true;
                this.updateEmptyState(isFromSearch);
            },
            err => console.log('Error', err)
        );
    }

    private getApiParams(): EventsRegistrationParams {
        const params = { ...this.params };

        for (const key in params) {
            if (Array.isArray(params[key])) {
                params[key] = params[key].join(',');
            }
        }

        return params;
    }

    private getItems(response: EventRegistrationsResponse): EmployersItems {
        const items = response.models.map(model => new StandardListPageModel({
            rowAnchor: undefined,
            id: model.registration_id,
            texts: this.getEmployerTexts(model),
            monogram: new Monogram({
                type: model.logo ? 'image' : 'icon',
                src: model.logo || 'icn-building',
                styleClass: 'avatar-md',
                autoColor: model.registration_id
            }),
            styleClass: 'list-item-responsive-actions',
            ariaLabel: model.name
        }));

        return { ...response, items };
    }

    private getEmployerTexts(model: EventRegistration): ListItemElement[] {
        const texts: ListItemElement[] = [
            new ListItemElement({
                label: model.name,
                defaultStyleType: 'title',
                styleClass: 'text-truncate-2'
            })
        ];

        let desc = '';
        if (this.isLawCsm) {
            desc = (model.employer_type && model.employer_type._label) || '';
        } else if (model.industry) {
            if (Array.isArray(model.industry)) {
                desc = model.industry.map(industry => industry._label).join(', ');
            } else {
                desc = model.industry['_label'];
            }
        }

        const label = this.isVirtualFair
            ? this.eventsService.getEventRegistrationAttendingLabel(model.reps_count, this.eventStatus)
            : this.eventsService.getEventRegistrationDatesLabel(model, this.eventStatus);

        if (desc) {
            texts.push(
                new ListItemElement({
                    label: desc,
                    defaultStyleType: 'subtitle',
                    styleClass: 'text-truncate'
                })
            );
        }

        texts.push(
            new ListItemElement({
                label,
                styleClass: 'list-data-columns text-gray font-weight-normal body-small text-gray'
            })
        );

        return texts;
    }

    private onRowAnchorClick(employerId: string): void {
        this.router.navigate([employerId], { relativeTo: this.route });
    }

    private updateEmptyState(isFromSearch: boolean): void {
        if (isFromSearch) {
            this.emptyState.title = 'event employers.No results found';
            this.emptyState.message = 'event employers.Please check that all words are spelled correctly or try different filters.';
            this.emptyState.src = '/components/valkyrie/images/empty-magnifying-glass-gray.svg';
        } else {
            this.emptyState.title = 'event employers.No employers attending yet';
            this.emptyState.message = 'event employers.When employers register to attend, you will see them listed here.';
            this.emptyState.src = '/components/valkyrie/images/empty-calendar-gray.svg';
        }
    }

    /** Filters related */
    public openMobileFilter(): void {
        const modal = this.modalService.open(DynamicFormMobileFilterComponent, {
            windowClass: 'ngb-modal modal-scroll',
            size: 'lg',
            backdrop: 'static',
            keyboard: false
        });

        modal.componentInstance.title = 'misc.Filters';
        modal.componentInstance.controls = this.filtersFormConfig.controls;
        modal.componentInstance.formGroup = this.eventsService.createFormGroup(this.filtersFormConfig.controls, this.params);
        modal.componentInstance.styleClass = 'more-filter-dropdown mobile-filter';

        modal.result.then(result => {
            if (result) {
                this.applyMultipleFilters(result);
            }
        });
    }

    public clearMultipleFilters(): void {
        this.clearFilter();
        this.dropdown.hide();
    }

    public closeMoreFiltersDropdown(): void {
        this.dropdown.hide();
    }

    public applySimpleFilter(selectedFilter: any): void {
        this.params[selectedFilter.id] = selectedFilter.fields
            .filter(field => field.value)
            .map(field => field.id);

        this.updateEmployersList();
    }

    public applyMultipleFilters(values: any): void {
        if (this.dropdown.isOpen) {
            this.dropdown.hide();
        }

        for (const key in values) {
            const value = values[key];
            const isArray = Array.isArray(value);

            if ((isArray && value.length) || (!isArray && value)) {
                this.params[key] = isArray ? value.map(item => item.hasOwnProperty('id') ? item.id : item) : value;
            } else if (this.params[key] && (!value || (isArray && !value.length))) {
                delete this.params[key];
            }
        }

        this.updateEmployersList();
    }

    public clearFilter(selectedFilter?: SelectedFilter): void {
        if (selectedFilter) {
            this.clearSelectedFilter(selectedFilter);
        } else {
            this.selectedFilters.forEach(selectedFilter => this.clearSelectedFilter(selectedFilter));
        }

        this.updateEmployersList();
    }

    private clearSelectedFilter(selectedFilter: SelectedFilter): void {
        if (selectedFilter.type === 'text-search') {
            delete this.params.kw;
            this.searchText$.next('');
            this.filters.filters.find(filter => filter.id === selectedFilter.id).value = '';
        } else {
            const key = selectedFilter.id;
            const value = this.params[key];

            if (Array.isArray(value) && value.length > 1) {
                const index = value.indexOf(selectedFilter.value);
                this.params[key].splice(index, 1);
            } else {
                delete this.params[key];
            }
        }
    }

    private getSimpleFilters(): any[] {
        return this.filtersFormConfig.controls
            .filter(control => control.isSimpleFilter)
            .map(control => new ListFilterMultipleSelect({
                id: control.id,
                title: control.label,
                fields: control.options.source.value.map(filter => {
                    return new ListFilterMultipleSelectField({
                        id: filter.id,
                        label: filter.value,
                        value: false
                    });
                })
            }));
    }

    private searchByKeywords(): void {
        const selectedFilter = this.filters.filters.find(filter => filter.type === 'text-search');
        const value = selectedFilter ? selectedFilter.value.trim() : '';

        if (value) {
            this.params.kw = value;
            this.searchText$.next(value);
        } else {
            delete this.params.kw;
            this.searchText$.next('');
        }

        this.updateEmployersList();
    }

    private updateEmployersList(): void {
        this.params.page = DEFAULT_PAGE;

        this.updateSelectedFilters();
        this.getEmployers();
    }

    private updateSelectedFilters(): void {
        const keywordsFilter = this.getSelectedKeywordsFilter();
        const simpleFilters = this.getSelectedSimpleFilters();
        const moreFilters = this.getSelectedMoreFilters();

        this.selectedFilters = [].concat(keywordsFilter, simpleFilters, moreFilters);
    }

    private getSelectedKeywordsFilter(): SelectedFilter[] {
        const searchText = this.searchText$.getValue();

        return searchText
            ? [ this.createSelectedFilter('text-search', searchText, searchText, 'text-search') ]
            : [];
    }

    private getSelectedSimpleFilters(): SelectedFilter[] {
        const selectedFilters: SelectedFilter[] = [];

        this.filters.filters
            .filter(filter => filter.fields)
            .map(filter => {
                const selectedFields = this.params[filter.id] || [];

                filter.fields.forEach(field => field.value = selectedFields.includes(field.id));

                return filter;
            })
            .forEach(filter => {
                const fields = filter.fields.filter(field => field.value);

                fields.forEach(field =>
                    selectedFilters.push(
                        this.createSelectedFilter(filter.id, field.label, field.id, filter.type)
                    )
                );
            });

        return selectedFilters;
    }

    private getSelectedMoreFilters(): SelectedFilter[] {
        const selectedFilters: SelectedFilter[] = [];
        const moreFilters = this.getMoreFiltersValues();

        for (const id in moreFilters) {
            const control = this.filtersFormConfig.controls.find(control => control.id === id);
            const value = moreFilters[id];

            if (Array.isArray(value)) {
                if (control.soWidget === 'hierpicklist') {
                    this.setHierpicklistSelectedFilters(id, control.options.source.value, value, selectedFilters, control.type);
                } else {
                    (Array.isArray(control.options) ? control.options : control.options.source.value).forEach(option => {
                        if (value.includes(option.id)) {
                            selectedFilters.push(
                                this.createSelectedFilter(id, option.value, option.id, control.type)
                            );
                        }
                    });
                }
            } else {
                let label = value;

                if (control.soWidget === 'yesno') {
                    label = `${control.label}: ${this.translationsService.instant(value == 1 ? 'misc.Yes' : 'misc.No')}`;
                }

                if (control.soWidget === 'select') {
                    label = control.options.source.value.find(option => option.id === value).value;
                }

                selectedFilters.push(
                    this.createSelectedFilter(id, label, value, control.type)
                );
            }
        }

        return selectedFilters;
    }

    private getMoreFiltersValues(): any {
        const values = { ...this.params };

        delete values.page;
        delete values.perPage;
        delete values.kw;

        this.eventsService.simpleFiltersIds.forEach(key => delete values[key]);

        return values;
    }

    public setHierpicklistSelectedFilters(
        id: string,
        source: any[],
        value: any[],
        selectedFilters: SelectedFilter[],
        controlType: string
    ): void {
        source.forEach(option => {
            if (value.includes(option.id)) {
                selectedFilters.push(
                    this.createSelectedFilter(id, option.value, option.id, controlType)
                );
            }

            if (Array.isArray(option.children) && option.children.length) {
                this.setHierpicklistSelectedFilters(id, option.children, value, selectedFilters, controlType);
            }
        });
    }

    private createSelectedFilter(
        id: string,
        label: string,
        value: string,
        type: string
    ): SelectedFilter {
        return new SelectedFilter({ id, label, value, type });
    }
}
